import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Clock3 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useShopPath } from '@/hooks/useShopPath';
import { useChannelActions } from '@/hooks/useChannelActions';
import myShopClient from '@services/nearlive';

const CHANNEL_ID = 'product-reservations';

const SUPPORT_URL = 'https://support.unify.near.st/orders-/7JASpDe6FvayPmBe32vX8m/what-are-reservations/7JASpDe6FxcEifMBSWP2wK';

// Exported so the comma/space/semicolon split + the email-shape check
// can be unit-tested in isolation — see ProductReservationsSetup.test.jsx.
export const parseEmails = (raw) =>
	raw
		.split(/[\s,;]+/)
		.map((entry) => entry.trim())
		.filter(Boolean);

export const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function ConfiguredEmails({ emails }) {
	if (!emails?.length) return null;
	return (
		<p className="text-sm text-muted-foreground">
			Currently set to: <span className="font-semibold text-foreground">{emails.join(', ')}</span>
		</p>
	);
}

/**
 * Customer Success handles the manual `pending → enabled` flip on this
 * channel, so retailers wait once they submit the form. The setup flow
 * mirrors the nearst dashboard: capture notification emails, fire a HubSpot
 * ticket for CS, and seed the channel as `pending`.
 */
export function ProductReservationsSetup({ channel, shop }) {
	const navigate = useNavigate();
	const { shopPath } = useShopPath();
	const { create, isBusy } = useChannelActions(shop.id);
	const existingEmails = channel.record?.config?.notificationEmails || [];
	const initialEmails = Array.isArray(existingEmails) ? existingEmails.join(', ') : String(existingEmails);
	const [emailsInput, setEmailsInput] = useState(initialEmails || shop.contactEmail || '');
	const [error, setError] = useState(null);

	const busy = isBusy(CHANNEL_ID);
	const isPending = channel.pending;
	const emails = parseEmails(emailsInput);
	const allValid = emails.length > 0 && emails.every(looksLikeEmail);

	const submit = async (event) => {
		event.preventDefault();
		if (!allValid || busy) return;
		setError(null);

		try {
			// HubSpot ticket creation is the signal for Customer Success to
			// finish setup server-side. The endpoint is a no-op outside
			// production (it logs and returns), so dev/staging are safe.
			const client = await myShopClient();
			await client.post(`/api/hubspot/tickets/${shop.id}`, {
				content: `Shop has enabled product reservations, please finish setup: https://hq.near.st/shops/${shop.legacyId}/channels`,
				setup_required: 'reservations'
			});
			await create(CHANNEL_ID, {
				config: {
					notificationEmails: emails,
					notificationPhoneNumbers: [],
					smsEnabled: false
				},
				status: 'pending'
			});
			// Stay on the setup page — the channel is now `pending` so the
			// "We're getting you set up" view takes over via channel.pending.
			navigate(shopPath(`/channels/${CHANNEL_ID}/setup`), { replace: true });
		} catch (err) {
			setError(err?.message || 'Something went wrong submitting the setup request.');
		}
	};

	if (isPending) {
		return (
			<div className="space-y-6">
				<div className="flex items-start gap-4">
					<div className="rounded-full bg-action-soft p-3 text-action-soft-foreground">
						<Clock3 className="h-5 w-5" />
					</div>
					<div className="space-y-1.5">
						<h2 className="text-lg font-bold text-foreground">We're getting you set up</h2>
						<p className="text-sm text-muted-foreground">
							Customer Success will be in touch if they need anything else. Once your store is configured, Product
							Reservations will switch on automatically.
						</p>
					</div>
				</div>

				<ConfiguredEmails emails={existingEmails} />

				<div className="flex flex-wrap items-center gap-3 pt-2">
					<Link
						to={shopPath(`/channels/${CHANNEL_ID}`)}
						className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-sm font-semibold text-background hover:opacity-90"
					>
						Back to channel overview <ArrowRight className="h-3.5 w-3.5" />
					</Link>
					<a
						href={SUPPORT_URL}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center text-sm font-semibold text-action hover:underline"
					>
						Read the Product Reservations docs
					</a>
				</div>
			</div>
		);
	}

	return (
		<form onSubmit={submit} className="space-y-6">
			<div className="space-y-2">
				<div className="flex items-center gap-2 text-sm font-semibold text-action">
					<CheckCircle2 className="h-4 w-4" /> Just one thing to get started
				</div>
				<h2 className="text-lg font-bold text-foreground">Where should customer reservations be sent?</h2>
				<p className="text-sm text-muted-foreground">
					When a customer reserves a product in-store, we'll email these addresses with the order details. You can update them any
					time from the channel manage page.
				</p>
			</div>

			<div className="max-w-md space-y-2">
				<Label htmlFor="reservation-emails">Notification email addresses</Label>
				<Input
					id="reservation-emails"
					type="text"
					autoComplete="off"
					placeholder="orders@yourstore.com, manager@yourstore.com"
					value={emailsInput}
					onChange={(event) => setEmailsInput(event.target.value)}
				/>
				<p className="text-xs text-muted-foreground">Separate multiple addresses with a comma.</p>
				{!allValid && emails.length > 0 ? (
					<p className="text-xs font-semibold text-destructive">Please check the email addresses — one or more look invalid.</p>
				) : null}
			</div>

			{error ? <p className="text-sm font-semibold text-destructive">{error}</p> : null}

			<div className="flex flex-wrap items-center gap-3">
				<Button type="submit" disabled={busy || !allValid}>
					{busy ? 'Submitting…' : 'Submit for setup'}
					<ArrowRight className="ml-2 h-3.5 w-3.5" />
				</Button>
				<a href={SUPPORT_URL} target="_blank" rel="noreferrer" className="text-sm font-semibold text-action hover:underline">
					Read the Product Reservations docs
				</a>
			</div>
		</form>
	);
}
